import { useEffect, useMemo, useRef, useState } from "react"
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from "~/react/ui/alert"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { NumberPicker } from "~/react/ui/number-picker"
import { CurrencyHelper } from "~/helpers/currency_helper"
import type { Suggestion } from "~/react/components/email_accounts/types"
import { Checkbox } from "~/react/ui/checkbox"
import { cn } from "@/lib/utils"
import { InputGroup } from "~/react/ui/input-group"
import { Hint } from "~/react/ui/hint"
import { Label } from "~/react/ui/label"
import { Input } from "~/react/ui/input"
import { Separator } from "~/react/ui/separator"
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "~/react/ui/tooltip"
import { OptionsGroup, OptionItem } from "~/react/ui/option-item"

type Props = {
  baseDomain: string
  domainsCount: number
  suggestions: Suggestion[]
  selectedMap: Record<string, boolean>
  showManualForm: boolean
  manualDomain: string
  isGenerating: boolean
  isCheckingManual: boolean
  manualError?: string
  flowError?: string
  redirectUrl: string
  isWebmail: boolean
  onBaseDomainChange: (value: string) => void
  onDomainsCountChange: (value: number) => void
  onToggleManualForm: (show: boolean) => void
  onManualDomainChange: (value: string) => void
  onAddManualDomain: () => void | Promise<void>
  onGenerateSuggestions: (opts?: { replace?: boolean }) => void
  onRemoveSuggestion: (id: string) => void
  onToggleSuggestion: (id: string, checked: boolean) => void
  onRedirectUrlChange: (value: string) => void
  currencyCode?: string
  existingDomains?: string[]
  flowMode?: "full" | "domains_only" | "mailboxes_only"
}

export default function DomainsTab({
  baseDomain,
  domainsCount,
  suggestions,
  selectedMap,
  showManualForm,
  manualDomain,
  isGenerating,
  isCheckingManual,
  manualError,
  flowError,
  redirectUrl,
  isWebmail,
  onBaseDomainChange,
  onDomainsCountChange,
  onToggleManualForm,
  onManualDomainChange,
  onAddManualDomain,
  onGenerateSuggestions,
  onRemoveSuggestion,
  onToggleSuggestion,
  onRedirectUrlChange,
  currencyCode,
  existingDomains = [],
}: Props) {
  const [generateBtnWidth, setGenerateBtnWidth] = useState<number | undefined>(
    undefined,
  )
  const generateBtnRef = useRef<HTMLButtonElement | null>(null)
  const [baseDomainError, setBaseDomainError] = useState<boolean>(false)
  const [baseDomainValidated, setBaseDomainValidated] = useState<boolean>(false)
  const [redirectUrlTouched, setRedirectUrlTouched] = useState<boolean>(false)
  const [redirectUrlDomain, setRedirectUrlDomain] = useState<string>("")
  const [manualDomainValidated, setManualDomainValidated] =
    useState<boolean>(false)
  const [showInlineManualForm, setShowInlineManualForm] =
    useState<boolean>(false)
  const [showBaseDomainForm, setShowBaseDomainForm] = useState<boolean>(false)
  const [suggestionsCountBeforeGenerate, setSuggestionsCountBeforeGenerate] =
    useState<number | null>(null)
  const [showNoNewDomainsHint, setShowNoNewDomainsHint] =
    useState<boolean>(false)
  const validExistingDomains = useMemo(
    () => existingDomains.filter((domain) => domain && domain.trim()),
    [existingDomains],
  )
  const [domainsSource, setDomainsSource] = useState<"existing" | "new">(
    validExistingDomains.length > 0 ? "existing" : "new",
  )

  useEffect(() => {
    if (!isGenerating && generateBtnRef.current) {
      setGenerateBtnWidth(generateBtnRef.current.offsetWidth)
    }
  }, [isGenerating])

  // Track when generation completes and check if new domains were added
  useEffect(() => {
    if (suggestionsCountBeforeGenerate === null) return

    if (!isGenerating) {
      // Generation just completed
      const currentReadyCount = suggestions.filter(
        (s) => s.status === "ready",
      ).length
      const previousReadyCount = suggestionsCountBeforeGenerate

      if (currentReadyCount === previousReadyCount) {
        // No new domains were added
        setShowNoNewDomainsHint(true)
        // Hide the hint after 5 seconds
        const timer = setTimeout(() => {
          setShowNoNewDomainsHint(false)
        }, 5000)
        return () => clearTimeout(timer)
      } else {
        // New domains were added, hide the hint
        setShowNoNewDomainsHint(false)
      }

      setSuggestionsCountBeforeGenerate(null)
    }
  }, [isGenerating, suggestions, suggestionsCountBeforeGenerate])

  const selectedDomains = useMemo(
    () =>
      suggestions
        .filter((s) => s.status === "ready" && selectedMap[s.id])
        .map((s) => s.domain as string),
    [suggestions, selectedMap],
  )

  useEffect(() => {
    setBaseDomainError(false)
    setBaseDomainValidated(false)
  }, [baseDomain])

  const isValidRedirectUrl = (url: string): boolean => {
    if (!url || !url.trim()) return false
    try {
      const u = new URL(url)
      const hostname = u.hostname
      const hasValidProtocol = u.protocol === "http:" || u.protocol === "https:"
      const hasDotInHost = hostname && hostname.includes(".")
      return hasValidProtocol && !!hasDotInHost
    } catch {
      return false
    }
  }

  const isRedirectUrlInvalid = useMemo(() => {
    if (!redirectUrl || !redirectUrlTouched) return false
    return !isValidRedirectUrl(redirectUrl)
  }, [redirectUrl, redirectUrlTouched])

  const redirectUrlLogo = useMemo(() => {
    if (!redirectUrlDomain || isRedirectUrlInvalid) return null
    return `https://company-logo.hunter.io/${redirectUrlDomain}`
  }, [redirectUrlDomain, isRedirectUrlInvalid])

  const handleRedirectUrlBlur = () => {
    setRedirectUrlTouched(true)
  }

  useEffect(() => {
    if (!redirectUrl || isRedirectUrlInvalid) {
      setRedirectUrlDomain("")
      return
    }

    try {
      const u = new URL(redirectUrl)
      const hostname = u.hostname
      const domain = hostname.replace(/^www\./, "")
      setRedirectUrlDomain(domain)
    } catch {
      setRedirectUrlDomain("")
    }
  }, [redirectUrl, isRedirectUrlInvalid])

  const isValidDomain = (domain: string): boolean => {
    if (!domain || !domain.trim()) return false
    const trimmed = domain.trim().toLowerCase()
    const cleaned = trimmed.replace(/^@/, "")
    const withoutProtocol = cleaned
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
    const domainOnly = withoutProtocol.split("/")[0]
    const domainRegex =
      /^(((?!\-))(xn\-\-)?[a-z0-9\-_]{0,61}[a-z0-9]{1,1}\.)*(xn\-\-)?([a-z0-9\-]{1,61}|[a-z0-9\-]{1,30})\.[a-z]{2,}$/
    return domainRegex.test(domainOnly)
  }

  const isDomainInvalid = (domain: string, validated: boolean): boolean => {
    if (!validated || !domain.trim()) return false
    return !isValidDomain(domain)
  }

  const isBaseDomainInvalid = useMemo(
    () => isDomainInvalid(baseDomain, baseDomainValidated),
    [baseDomain, baseDomainValidated],
  )

  const isManualDomainInvalid = useMemo(() => {
    if (!manualDomain.trim() || !manualDomainValidated) return false
    return !isValidDomain(manualDomain)
  }, [manualDomain, manualDomainValidated])

  const handleBaseDomainBlur = () => {
    setBaseDomainValidated(true)
  }

  const handleManualDomainBlur = () => {
    setManualDomainValidated(true)
  }

  const isRegenerate =
    (selectedDomains.length > 0 || suggestions.length > 0) && !isGenerating
  const regenerateTitle = isRegenerate
    ? i18n.t("js.email_accounts.provisioning.regenerate_warning")
    : undefined
  const isBaseDomainEmpty = !(baseDomain || "").trim()
  const isGenerateDisabled =
    isGenerating || isBaseDomainEmpty || isBaseDomainInvalid
  const showLoadingState =
    isGenerating && !isBaseDomainEmpty && !isBaseDomainInvalid

  const handleGenerateClick = () => {
    setBaseDomainValidated(true)
    if (isBaseDomainEmpty) {
      setBaseDomainError(true)
      const el = document.getElementById(
        "base-domain",
      ) as HTMLInputElement | null
      el?.focus()
      el?.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }
    if (isBaseDomainInvalid) {
      setBaseDomainError(true)
      const el = document.getElementById(
        "base-domain",
      ) as HTMLInputElement | null
      el?.focus()
      el?.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }
    const hasExistingSuggestions =
      suggestions.length > 0 || selectedDomains.length > 0
    onGenerateSuggestions({ replace: hasExistingSuggestions })
  }

  const handleGenerateMoreClick = () => {
    setBaseDomainValidated(true)
    if (isBaseDomainEmpty) {
      setBaseDomainError(true)
      const el = document.getElementById(
        "base-domain",
      ) as HTMLInputElement | null
      el?.focus()
      el?.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }
    if (isBaseDomainInvalid) {
      setBaseDomainError(true)
      const el = document.getElementById(
        "base-domain",
      ) as HTMLInputElement | null
      el?.focus()
      el?.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }
    // Track the count of ready suggestions before generating
    const readyCount = suggestions.filter((s) => s.status === "ready").length
    setSuggestionsCountBeforeGenerate(readyCount)
    setShowNoNewDomainsHint(false)
    onGenerateSuggestions()
  }

  const clearRedirectUrl = () => {
    onRedirectUrlChange("")
  }

  const handleOriginalBaseDomain = () => {
    setShowBaseDomainForm(!showBaseDomainForm)
  }

  const currencySymbol = useMemo(() => {
    return CurrencyHelper.currencySymbol(currencyCode || "usd")
  }, [currencyCode])

  const hasExistingDomains =
    existingDomains.filter((domain) => domain && domain.trim()).length > 0

  return (
    <TooltipProvider>
      <div className="email-accounts-provisioning">
        <h4 className="text-lg-medium mb-1">
          {i18n.t("js.email_accounts.provisioning.choose_domains_title")}
        </h4>
        <p className="text-xs text-body-secondary">
          {i18n.t("js.email_accounts.provisioning.choose_domains_description")}
        </p>

        <Alert size="small" className="mt-4 mb-6">
          <AlertIcon>
            <Icon
              size="sm"
              className="text-hint"
              icon="info-circle"
              aria-hidden="true"
            />
          </AlertIcon>
          <AlertContent>
            <AlertTitle className="h-alert__title">
              {i18n.t("js.email_accounts.provisioning.info_title")}
            </AlertTitle>
            <AlertDescription
              dangerouslySetInnerHTML={{
                __html: i18n.t(
                  "js.email_accounts.provisioning.info_description_html",
                ),
              }}
            />
          </AlertContent>
        </Alert>

        {hasExistingDomains && (
          <>
            <OptionsGroup orientation="horizontal" className="mb-6">
              <OptionItem
                id="domains-source-existing"
                name="domains-source"
                value="existing"
                checked={domainsSource === "existing"}
                onChange={() => {
                  setDomainsSource("existing")
                  // Auto-select all existing domains when switching to existing
                  validExistingDomains.forEach((domain) => {
                    const suggestionId = `existing-${domain}`
                    if (!selectedMap[suggestionId]) {
                      onToggleSuggestion(suggestionId, true)
                    }
                  })
                }}
                label={i18n.t(
                  "js.email_accounts.provisioning.domains_source.existing.title",
                )}
                description={i18n.t(
                  "js.email_accounts.provisioning.domains_source.existing.description",
                  {
                    count: validExistingDomains.length.toString(),
                    domain_name:
                      validExistingDomains.length === 1
                        ? validExistingDomains[0]
                        : undefined,
                  },
                )}
                className="align-items-start"
              />
              <OptionItem
                id="domains-source-new"
                name="domains-source"
                value="new"
                checked={domainsSource === "new"}
                onChange={() => {
                  setDomainsSource("new")
                  // Unselect all existing domains when switching to "new"
                  validExistingDomains.forEach((domain) => {
                    const suggestionId = `existing-${domain}`
                    if (selectedMap[suggestionId]) {
                      onToggleSuggestion(suggestionId, false)
                    }
                  })
                }}
                label={i18n.t(
                  "js.email_accounts.provisioning.domains_source.new.title",
                )}
                description={i18n.t(
                  "js.email_accounts.provisioning.domains_source.new.description",
                )}
                className="align-items-start"
              />
            </OptionsGroup>

            {domainsSource === "existing" && (
              <div className="domain-suggestions-list mb-6">
                {validExistingDomains.map((domain) => {
                  const suggestionId = `existing-${domain}`
                  const isSelected = selectedMap[suggestionId] || false

                  return (
                    <div key={domain} className="domain-suggestion">
                      <Checkbox
                        id={`existing-domain-${domain}`}
                        checked={isSelected}
                        onChange={(e) =>
                          onToggleSuggestion(
                            suggestionId,
                            (e as any).target.checked,
                          )
                        }
                        aria-label={domain}
                      />
                      <Label
                        htmlFor={`existing-domain-${domain}`}
                        className="flex-grow-1 text-xs-medium mb-0"
                      >
                        {domain}
                      </Label>
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}

        {domainsSource !== "existing" && (
          <section className="mb-6">
            {!isWebmail ||
            (baseDomain && baseDomain.trim() && suggestions.length > 0) ? (
              <h4 className="text-md-medium mb-1">
                {i18n.t("js.email_accounts.provisioning.recommended_domains")}
              </h4>
            ) : (
              <h4 className="text-md-medium mb-1">
                {i18n.t(
                  "js.email_accounts.provisioning.domain_selection_title",
                )}
              </h4>
            )}
            <p className="text-xs text-body-secondary mb-4">
              {i18n.t(
                "js.email_accounts.provisioning.domain_selection_description",
              )}
            </p>
            {showManualForm ? (
              <>
                <InputGroup
                  id="manual-domain"
                  value={manualDomain}
                  placeholder={i18n.t(
                    "js.email_accounts.provisioning.base_domain_placeholder",
                  )}
                  error={
                    manualError ||
                    (isManualDomainInvalid
                      ? i18n.t(
                          "js.email_accounts.provisioning.invalid_domain_format",
                        )
                      : undefined)
                  }
                  isInvalid={isManualDomainInvalid}
                  isLoading={isCheckingManual}
                  buttonText={i18n.t(
                    "js.email_accounts.provisioning.add_domain",
                  )}
                  loadingText={i18n.t(
                    "js.email_accounts.provisioning.checking",
                  )}
                  label={i18n.t(
                    "js.email_accounts.provisioning.manual_domain_label",
                  )}
                  showFieldWrapper={true}
                  inputProps={{ size: "sm" }}
                  onChange={onManualDomainChange}
                  onBlur={handleManualDomainBlur}
                  onSubmit={onAddManualDomain}
                />

                <p className="text-body-secondary text-2xs mt-2">
                  {i18n.t("js.email_accounts.provisioning.not_sure")}
                  <Button
                    className="ml-1"
                    variant="linkGrey"
                    size="xs"
                    onClick={() => onToggleManualForm(false)}
                  >
                    {i18n.t(
                      "js.email_accounts.provisioning.generate_suggestions",
                    )}
                  </Button>
                </p>
              </>
            ) : (
              <>
                {!isWebmail &&
                  baseDomain &&
                  baseDomain.trim() &&
                  suggestions.length > 0 && (
                    <div className="text-2xs text-body-secondary mb-2">
                      {i18n.t("js.email_accounts.provisioning.generated_from", {
                        baseDomain,
                      })}
                      <Button
                        variant="linkGrey"
                        size="xs"
                        onClick={() => handleOriginalBaseDomain()}
                      >
                        {showBaseDomainForm
                          ? i18n.t("js.email_accounts.provisioning.close")
                          : i18n.t(
                              "js.email_accounts.provisioning.change_base_domain",
                            )}
                      </Button>
                      <Icon
                        icon={showBaseDomainForm ? "angle-up" : "angle-down"}
                        size="xs"
                        aria-hidden="true"
                      />
                    </div>
                  )}
                <form
                  className={cn(
                    "mb-4",
                    !isWebmail &&
                      baseDomain &&
                      baseDomain.trim() &&
                      suggestions.length > 0 &&
                      "base-domain-form",
                    !isWebmail &&
                      baseDomain &&
                      baseDomain.trim() &&
                      suggestions.length > 0 &&
                      !showBaseDomainForm &&
                      "d-none",
                  )}
                  onSubmit={(e) => {
                    e.preventDefault()
                    handleGenerateClick()
                  }}
                >
                  <Label htmlFor="base-domain">
                    {i18n.t("js.email_accounts.provisioning.base_domain_label")}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="question-circle ml-1"
                          aria-label={i18n.t(
                            "js.email_accounts.provisioning.base_domain_hint",
                          )}
                        >
                          <Icon
                            icon="circle-question"
                            size="xs"
                            aria-hidden="true"
                          />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {i18n.t(
                          "js.email_accounts.provisioning.base_domain_hint",
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <div className="d-flex gap-2">
                    <div className="domain-suggestion-input">
                      <Input
                        id="base-domain"
                        type="text"
                        placeholder={i18n.t(
                          "js.email_accounts.provisioning.base_domain_placeholder",
                        )}
                        value={baseDomain}
                        onChange={(e) => {
                          onBaseDomainChange(e.target.value)
                          if (
                            (baseDomainError || isBaseDomainInvalid) &&
                            (e.target.value || "").trim()
                          ) {
                            setBaseDomainError(false)
                            setBaseDomainValidated(false)
                          }
                        }}
                        onBlur={handleBaseDomainBlur}
                        aria-invalid={
                          baseDomainError || isBaseDomainInvalid
                            ? "true"
                            : undefined
                        }
                        className={cn(
                          baseDomainError || isBaseDomainInvalid
                            ? "error"
                            : undefined,
                          "domain-suggestion-input__input",
                        )}
                      />
                      <NumberPicker
                        size="sm"
                        value={domainsCount}
                        onChange={onDomainsCountChange}
                        min={1}
                        ariaLabelPrefix={i18n.t(
                          "js.email_accounts.provisioning.domains_quantity",
                        )}
                      />
                    </div>
                    {regenerateTitle ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="submit"
                            variant="default"
                            size="sm"
                            aria-busy={showLoadingState}
                            disabled={isGenerateDisabled}
                            ref={generateBtnRef}
                            style={
                              generateBtnWidth
                                ? { width: `${generateBtnWidth}px` }
                                : undefined
                            }
                            className="domain-suggestion-input__button"
                          >
                            {showLoadingState
                              ? i18n.t(
                                  "js.email_accounts.provisioning.generating",
                                )
                              : selectedDomains.length > 0 ||
                                  suggestions.length > 0
                                ? i18n.t(
                                    "js.email_accounts.provisioning.re_generate",
                                  )
                                : i18n.t(
                                    "js.email_accounts.provisioning.generate_suggestions",
                                  )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{regenerateTitle}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <Button
                        type="submit"
                        variant="default"
                        size="sm"
                        aria-busy={showLoadingState}
                        disabled={isGenerateDisabled}
                        ref={generateBtnRef}
                        style={
                          generateBtnWidth
                            ? { width: `${generateBtnWidth}px` }
                            : undefined
                        }
                        className="domain-suggestion-input__button"
                      >
                        {showLoadingState
                          ? i18n.t("js.email_accounts.provisioning.generating")
                          : selectedDomains.length > 0 || suggestions.length > 0
                            ? i18n.t(
                                "js.email_accounts.provisioning.re_generate",
                              )
                            : i18n.t(
                                "js.email_accounts.provisioning.generate_suggestions",
                              )}
                      </Button>
                    )}
                  </div>

                  {(baseDomainError || isBaseDomainInvalid) && (
                    <Hint variant="error" className="mt-1">
                      {baseDomainError
                        ? i18n.t(
                            "js.email_accounts.provisioning.base_domain_required",
                          )
                        : i18n.t(
                            "js.email_accounts.provisioning.invalid_domain_format",
                          )}
                    </Hint>
                  )}

                  <p
                    className={cn(
                      "text-body-secondary text-2xs mt-2",
                      suggestions.length > 0 && "d-none",
                    )}
                  >
                    {i18n.t(
                      "js.email_accounts.provisioning.prefer_manual_html",
                    )}
                    <Button
                      className="ml-1"
                      variant="linkGrey"
                      size="xs"
                      onClick={() => onToggleManualForm(true)}
                    >
                      {i18n.t("js.email_accounts.provisioning.add_manually")}
                    </Button>
                  </p>
                </form>
              </>
            )}
            {flowError && (
              <Alert variant="warning" className="mb-3">
                <AlertContent>{flowError}</AlertContent>
              </Alert>
            )}

            {suggestions.length > 0 && (
              <div className="domain-suggestions-list">
                {suggestions
                  .filter((s) => {
                    // When "new" is selected, filter out existing domains
                    if (
                      domainsSource === "new" &&
                      s.id.startsWith("existing-")
                    ) {
                      return false
                    }
                    return true
                  })
                  .map((s) => (
                    <>
                      {s.status === "loading" && (
                        <div
                          key={s.id}
                          className="domain-suggestion"
                          role="status"
                          aria-live="polite"
                          aria-atomic="true"
                        >
                          <Icon
                            icon="spinner-third"
                            size="sm"
                            className="fa-spin"
                            aria-hidden="true"
                          />
                          <span className="text-xs text-shimmer">
                            {i18n.t(
                              "js.email_accounts.provisioning.generating_domain",
                            )}
                          </span>
                        </div>
                      )}
                      {s.status === "error" && (
                        <div className="domain-suggestion text-danger">
                          <Icon
                            icon="circle-exclamation"
                            size="sm"
                            aria-hidden="true"
                          />
                          <span className="text-xs text-danger">
                            {i18n.t("js.email_accounts.provisioning.api_error")}
                          </span>
                        </div>
                      )}
                      {s.status === "ready" && (
                        <div className="domain-suggestion">
                          <Checkbox
                            id={`domain-suggestion-${s.id}`}
                            checked={!!selectedMap[s.id]}
                            onChange={(e) =>
                              onToggleSuggestion(
                                s.id,
                                (e as any).target.checked,
                              )
                            }
                            aria-label={s.domain}
                          />
                          <Label
                            htmlFor={`domain-suggestion-${s.id}`}
                            className="flex-grow-1 text-xs-medium mb-0"
                          >
                            {s.domain}
                          </Label>
                          <span className="text-xs">
                            {CurrencyHelper.numberToCurrency(
                              (s.priceCents || 0) / 100,
                              currencySymbol,
                            )}
                            <span className="text-body-secondary text-2xs">
                              /
                              {i18n.t(
                                "js.email_accounts.provisioning.per_year",
                              )}
                            </span>
                          </span>
                          <Button
                            variant="default"
                            iconOnly
                            size="xs"
                            aria-label={i18n.t(
                              "js.email_accounts.provisioning.remove_domain",
                            )}
                            title={i18n.t(
                              "js.email_accounts.provisioning.remove_domain",
                            )}
                            onClick={() => onRemoveSuggestion(s.id)}
                            className="ml-2"
                          >
                            <Icon icon="trash" size="sm" aria-hidden="true" />
                          </Button>
                        </div>
                      )}
                    </>
                  ))}

                {/* Manual form - shown after suggestions list, only when user clicks "Add manually" */}
                {showInlineManualForm && (
                  <InputGroup
                    id="manual-domain-inline"
                    value={manualDomain}
                    placeholder={i18n.t(
                      "js.email_accounts.provisioning.base_domain_placeholder",
                    )}
                    error={
                      manualError ||
                      (isManualDomainInvalid
                        ? i18n.t(
                            "js.email_accounts.provisioning.invalid_domain_format",
                          )
                        : undefined)
                    }
                    isInvalid={isManualDomainInvalid}
                    isLoading={isCheckingManual}
                    buttonText={i18n.t(
                      "js.email_accounts.provisioning.add_domain",
                    )}
                    loadingText={i18n.t(
                      "js.email_accounts.provisioning.checking",
                    )}
                    className="domain-suggestion domain-suggestion--manual"
                    inputProps={{ size: "sm" }}
                    onChange={onManualDomainChange}
                    onBlur={handleManualDomainBlur}
                    onSubmit={onAddManualDomain}
                  />
                )}

                <div className="d-flex flex-column gap-2 mt-2">
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="default"
                      onClick={handleGenerateMoreClick}
                      aria-busy={showLoadingState}
                      disabled={isGenerateDisabled}
                    >
                      {showLoadingState
                        ? i18n.t("js.email_accounts.provisioning.generating")
                        : i18n.t(
                            "js.email_accounts.provisioning.generate_more",
                            {
                              count: domainsCount,
                            },
                          )}
                    </Button>
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => {
                        setShowInlineManualForm(true)
                        setTimeout(() => {
                          const input = document.getElementById(
                            "manual-domain-inline",
                          ) as HTMLInputElement | null
                          input?.focus()
                          input?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          })
                        }, 0)
                      }}
                    >
                      {i18n.t("js.email_accounts.provisioning.add_manually")}
                    </Button>
                  </div>
                  {showNoNewDomainsHint && (
                    <Hint variant="error" className="mt-1">
                      {i18n.t(
                        "js.email_accounts.provisioning.no_new_domains_available",
                      )}
                    </Hint>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        <section className="mb-6">
          <Separator className="my-8" />
          <h4 className="text-md-medium mb-1">
            {i18n.t("js.email_accounts.provisioning.domain_redirection_title")}
          </h4>
          <p className="text-2xs text-body-secondary">
            {i18n.t(
              "js.email_accounts.provisioning.domain_redirection_description",
            )}
          </p>
          <Label>
            {i18n.t("js.email_accounts.provisioning.redirect_url_label")}
          </Label>
          <div className="domain-field domain-field--sm">
            {redirectUrlLogo && (
              <img
                className="domain-field__logo"
                src={redirectUrlLogo}
                alt=""
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = "none"
                }}
              />
            )}
            <Input
              type="url"
              placeholder={i18n.t(
                "js.email_accounts.provisioning.redirect_url_placeholder",
              )}
              value={redirectUrl}
              onChange={(e) => {
                onRedirectUrlChange(e.target.value)
                if (!e.target.value) {
                  setRedirectUrlTouched(false)
                }
              }}
              onBlur={handleRedirectUrlBlur}
              aria-invalid={isRedirectUrlInvalid}
              className="domain-field__input"
            />
            {redirectUrl.length > 0 && (
              <Button
                variant="ghost"
                iconOnly
                size="xs"
                aria-label={i18n.t(
                  "js.email_accounts.provisioning.redirect_url_clear",
                )}
                onClick={() => {
                  clearRedirectUrl()
                  setRedirectUrlTouched(false)
                  setRedirectUrlDomain("")
                }}
              >
                <Icon icon="times" size="sm" aria-hidden="true" />
              </Button>
            )}
          </div>
          {isRedirectUrlInvalid && (
            <Hint variant="error" className="mt-2">
              {i18n.t("js.email_accounts.provisioning.redirect_url_invalid")}
            </Hint>
          )}
        </section>
      </div>
    </TooltipProvider>
  )
}


